import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type {
  DruckverlustAnlage, DruckverlustPosten, DruckverlustStrang, DruckverlustUebersicht, LueftungBefund,
  LueftungBefundliste, LueftungGrundlagen, LueftungLaufparameter, LueftungPosten, LueftungTeilstreckenposten,
  LueftungWegteilstrecke, LueftungWegteilstrecken,
} from '../../api';
import type { Rahmen } from '../Projektrahmen';
import { PageHeader } from '../../rahmen/PageHeader';
import { GewerkUnterlagenZip } from '../../UnterlagenZipKnopf';
import {
  Baum, ContentSection, DataTable, Drawer, EmptyState, ErrorState, ExportLeiste, Feld, FilterBar, KpiCard, KpiReihe,
  LoadingState, StatusBadge, bestaetigung, formatiereZahl, spalteFuer, useFilterInUrl, type BaumKnoten,
  type FilterDefinition, type Spalte,
} from '../../muster';
import { ANNAHMEN_ANKER, ANNAHMEN_TITEL, rechenstandText, schwereStatus } from './pruefung';
import {
  anlagenKnoten, postenherkunft, teilstreckenKnoten, wegId,
  type Druckverlustknoten,
} from './druckverlustregeln';
import { Berichtsvorbehaltkasten, useBerichtsvorbehalte } from './LueftungBerichtsvorbehalte';
import './druckverlust.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DRUCKVERLUST JE LÜFTUNGSANLAGE (TV6a, TX-STANDARD 1.6 S1)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Derselbe Aufbau wie vorher und wie in der Netzberechnung von LINEAR: links
 * die Auswahl (Anlage → Weg → Teilstrecke), rechts der Nachweis - der
 * maßgebende Strang Posten für Posten, die Verbraucher des Wegs und die
 * Befunde der Grenzwertprüfung. Eine Druckverlustrechnung wird für EINE
 * Anlage geprüft; der Vergleich über alle steht in den Kennzahlen oben.
 *
 * Neu mit TV6a ist, dass alles über die Muster läuft:
 * - `PageHeader` mit Rechenstand und Exportleiste (PDF LUE-DRV, Unterlagen-ZIP)
 *   und dem Rechenknopf;
 * - die Vorbehalte über den Zahlen als Muster `Vorbehalte`, Satz für Satz wie
 *   der Kasten im PDF LUE-DRV (fehlende Beiwerte, vermaschtes Netz,
 *   Zwischenstand), dazu der Satz zu den unbestätigten Laufparametern;
 * - `Baum` ohne Kappung (vorher 30 Teilstrecken mit „alle zeigen“),
 *   `DataTable` statt roher Tabellen (vorher 25 Verbraucher), Summenfuß nach
 *   der Fehlstellenregel: Fehlt ein Beiwert, steht die Summe als „≥“;
 * - die Herkunft je Eingangswert als Marke mit Wort statt Buchstabe mit Tooltip;
 * - Zahlen über `formatiereZahl` (vorher `toFixed` mit Dezimalpunkt);
 * - Abschnitt „Annahmen und Vorgaben“ mit den Laufparametern - weiter änderbar,
 *   ein Wert gilt ab dem nächsten Rechenlauf.
 *
 * Der Luftmengenvergleich (vorher hier, gekappt auf 200 von 978 Zeilen, mit
 * dem Raumstandhinweis der Luftmengen) steht vollständig auf der Seite
 * „Luftmengen“; alle Befunde des Laufs stehen auf „Befunde Kanalnetz und
 * Raumpegel“. Diese Seite verweist dorthin.
 */
export function Druckverlust() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [uebersicht, setUebersicht] = useState<DruckverlustUebersicht | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [grundlagen, setGrundlagen] = useState<LueftungGrundlagen | null>(null);
  const [gewaehlt, setGewaehlt] = useState<DruckverlustAnlage | null>(null);
  const [teilstrecke, setTeilstrecke] = useState<LueftungWegteilstrecke | null>(null);
  const [rechnet, setRechnet] = useState(false);
  const [rechenfehler, setRechenfehler] = useState<unknown>(null);
  const [gerechnet, setGerechnet] = useState(false);
  // Zählt die Läufe dieser Sitzung: Nach „Alle Anlagen rechnen“ laden Baum und Nachweis neu.
  const [lauf, setLauf] = useState(0);
  // TV7a-2 (§22 c): der Vorbehaltskasten des Berichts LUE-DRV (Übersicht), Satz für Satz wie im PDF.
  const berichtsvorbehalte = useBerichtsvorbehalte(api, projekt.id, 'druckverlust', {}, lauf);

  // Die Teilstrecken eines Wegs lesen zwei Stellen - der Baum (dritte Ebene)
  // und die Verbraucherliste rechts. Sie werden je Weg und Lauf EINMAL geladen.
  const teilstreckenCache = useRef(new Map<string, Promise<LueftungWegteilstrecken>>());
  const teilstreckenLaden = useCallback((a: { anlage: string; weg: string }) => {
    const id = wegId(a);
    let p = teilstreckenCache.current.get(id);
    if (!p) {
      p = api.lueftungTeilstrecken(projekt.id, a.anlage, a.weg);
      p.catch(() => teilstreckenCache.current.delete(id));
      teilstreckenCache.current.set(id, p);
    }
    return p;
  }, [api, projekt.id]);

  const laden = useCallback(() => {
    setFehler(null);
    teilstreckenCache.current.clear();
    api.druckverlustUebersicht(projekt.id).then(setUebersicht).catch(setFehler);
  }, [api, projekt.id]);

  // Die Grundlagen mit eigenem catch: Ohne sie fehlt nur der Abschnitt „Annahmen und Vorgaben“.
  const grundlagenLaden = useCallback(() => {
    api.lueftungGrundlagen(projekt.id).then(setGrundlagen).catch(() => setGrundlagen(null));
  }, [api, projekt.id]);

  useEffect(() => { laden(); grundlagenLaden(); }, [laden, grundlagenLaden]);

  const rechnen = async () => {
    setRechnet(true);
    setRechenfehler(null);
    setGerechnet(false);
    try {
      await api.druckverlustRechnen(projekt.id);
      setTeilstrecke(null);
      setLauf((n) => n + 1);
      setGerechnet(true);
      laden();
    } catch (e) {
      setRechenfehler(e);
    } finally {
      setRechnet(false);
    }
  };

  const filter = useMemo<FilterDefinition[]>(() => [
    { schluessel: 'suche', beschriftung: 'Anlage, Kreis oder Raum', art: 'text' },
    { schluessel: 'vorbehalt', beschriftung: 'nur Wege mit Vorbehalt', art: 'haken' },
  ], []);
  const [werte, setWerte] = useFilterInUrl(filter);

  const knoten = useMemo(() => anlagenKnoten(uebersicht?.anlagen ?? [], {
    suche: typeof werte.suche === 'string' ? werte.suche : '', nurVorbehalt: werte.vorbehalt === true,
  }), [uebersicht, werte]);

  const ladeKinder = useCallback(async (k: BaumKnoten<Druckverlustknoten>) => {
    if (k.daten?.art !== 'weg') return [];
    const a = k.daten.anlage;
    return teilstreckenKnoten(a, (await teilstreckenLaden(a)).teilstrecken);
  }, [teilstreckenLaden]);

  const waehle = (k: BaumKnoten<Druckverlustknoten>) => {
    if (k.daten?.art === 'weg') { setGewaehlt(k.daten.anlage); setTeilstrecke(null); }
    if (k.daten?.art === 'teilstrecke') { setGewaehlt(k.daten.anlage); setTeilstrecke(k.daten.teilstrecke); }
  };

  const u = uebersicht;
  const ohneLauf = !!u && u.summe.anlagen === 0 && u.anlagen.length === 0;
  const hoechste = (u?.anlagen ?? []).reduce<DruckverlustAnlage | null>(
    (m, a) => (a.druckverlustPa !== null && (m === null || a.druckverlustPa > (m.druckverlustPa ?? 0)) ? a : m),
    null);
  const gewaehltId = teilstrecke ? `t:${teilstrecke.id}` : gewaehlt ? wegId(gewaehlt) : null;

  return (
    <>
      <PageHeader
        titel="Druckverlust"
        beschreibung="Druckverlust je Lüftungsanlage und Weg aus dem Kanalnetzlauf: maßgebender Strang, Verbraucher und Teilstrecken, mit den Grundlagen der Rechnung."
        meta={(
          <span data-rechenstand>
            {rechenstandText(u?.stand
              ? { lauf: 'Druckverlustlauf', werkzeug: 'druckverlust-lueftung', gerechnetAm: u.stand, modellstand: null, datenstand: '' }
              : null)}
          </span>
        )}
        aktionen={(
          <>
            <button type="button" className="m-knopf" onClick={() => void rechnen()} disabled={rechnet || !u}>
              {rechnet ? 'rechnet …' : 'Alle Anlagen rechnen'}
            </button>
            <ExportLeiste ziele={[
              {
                art: 'pdf', beschriftung: 'PDF LUE-DRV', laden: () => api.druckverlustPdf(projekt.id),
                gesperrt: !u || u.summe.anlagen === 0 ? { grund: 'Noch kein Druckverlustlauf.' } : undefined,
              },
            ]}
            />
            <GewerkUnterlagenZip api={api} projektId={projekt.id} gewerk="lueftung" />
          </>
        )}
      />

      <p className="lue-dv-laufwerte" role="status" data-meldung="lauf">
        {gerechnet ? 'Alle Anlagen gerechnet; Übersicht, Baum und Nachweis sind neu geladen.' : ''}
      </p>

      {rechenfehler !== null && (
        <ErrorState klein meldung="Die Rechnung ist gescheitert." technisch={rechenfehler}
                    beiErneutVersuchen={() => void rechnen()} />
      )}

      {!u && fehler === null && <LoadingState text="Druckverlust wird abgerufen …" zeilen={8} />}
      {fehler !== null && (
        <ErrorState meldung="Der Druckverlust konnte nicht abgerufen werden." technisch={fehler}
                    beiErneutVersuchen={laden} />
      )}

      {ohneLauf && (
        <EmptyState
          titel="Noch kein Druckverlust gerechnet."
          erklaerung="Der Druckverlust entsteht im Kanalnetzlauf (druckverlust-lueftung) aus dem Kanalnetz der Lüftungsaufnahme. Mit „Alle Anlagen rechnen“ läuft er für alle Anlagen."
        />
      )}

      {u && !ohneLauf && (
        <>
          <Berichtsvorbehaltkasten stand={berichtsvorbehalte} bericht="druckverlust" />

          <KpiReihe>
            <KpiCard titel="Anlagen gerechnet" wert={u.summe.anlagen}
                     vergleich={`${formatiereZahl(u.anlagen.length, 0)} Wege`} />
            <KpiCard
              titel="höchster Druckverlust" wert={hoechste?.druckverlustPa ?? null} einheit="Pa" nachkomma={0}
              vergleich={hoechste ? `${hoechste.anlage} · ${hoechste.weg}` : undefined}
            />
            <KpiCard titel="fehlende Beiwerte" wert={u.summe.fehlendeBeiwerte}
                     vergleich={u.summe.fehlendeBeiwerte > 0 ? 'Summe dort zu klein' : undefined} />
            <KpiCard titel="Wege auf vermaschtem Netz" wert={u.summe.ringnetze}
                     vergleich={u.summe.ringnetze > 0 ? 'untere Schranke' : undefined} />
            <KpiCard titel="Wege ohne Ergebnis" wert={u.summe.ohneErgebnis} />
          </KpiReihe>

          <div className="lue-dv-teilung">
            <div className="lue-dv-baum">
              <ContentSection
                titel="Anlagen und Wege"
                beschreibung="Eine Anlage aufklappen und einen Weg wählen; eine Teilstrecke zeigt den Strang genau dieses Verbrauchers."
                werkzeuge={<FilterBar filter={filter} werte={werte} beiAenderung={setWerte} inLeiste={2} />}
              >
                <Baum<Druckverlustknoten>
                  key={`baum-${lauf}`}
                  name="Anlagen, Wege und Teilstrecken"
                  knoten={knoten}
                  ladeKinder={ladeKinder}
                  gewaehlt={gewaehltId}
                  beiWahl={waehle}
                  leer={<span>Kein Weg passt zu den Filtern.</span>}
                />
              </ContentSection>
            </div>
            <div className="lue-dv-detail">
              {!gewaehlt && (
                <ContentSection titel="Nachweis">
                  <EmptyState
                    klein
                    titel="Noch kein Weg gewählt."
                    erklaerung="Links eine Anlage aufklappen und einen Weg wählen. Hier steht dann sein maßgebender Strang - Posten für Posten, mit den Eingangsgrößen jeder Zeile."
                  />
                </ContentSection>
              )}
              {gewaehlt && teilstrecke && (
                <Teilstreckendetail
                  key={`${teilstrecke.id}|${lauf}`}
                  projektId={projekt.id}
                  teilstrecke={teilstrecke}
                  zurueck={() => setTeilstrecke(null)}
                />
              )}
              {gewaehlt && !teilstrecke && (
                <Wegdetail
                  key={`${wegId(gewaehlt)}|${lauf}`}
                  projektId={projekt.id}
                  anlage={gewaehlt}
                  teilstreckenLaden={teilstreckenLaden}
                  waehleTeilstrecke={setTeilstrecke}
                />
              )}
            </div>
          </div>

          <ContentSection
            titel="Weitere Ergebnisse des Laufs"
            beschreibung="Luftmengen und Befunde stehen auf eigenen Seiten - vollständig und mit ihren Vorbehalten."
          >
            <p className="lue-dv-laufwerte">
              <Link to={`/projekte/${projekt.id}/lueftung/luftmengen`}>Luftmengen je Raum</Link>
              {' · '}
              <Link to={`/projekte/${projekt.id}/lueftung/pruefbefunde`}>Befunde Kanalnetz und Raumpegel</Link>
            </p>
          </ContentSection>

          <Laufparameter
            daten={grundlagen}
            setzen={async (schluessel, wert) => {
              await api.lueftungLaufparameterSetzen(projekt.id, schluessel, { wert });
              grundlagenLaden();
            }}
          />
        </>
      )}
    </>
  );
}

// ═══ Rechts: der gewählte Weg ═════════════════════════════════════════════════

function Wegdetail({
  projektId, anlage, teilstreckenLaden, waehleTeilstrecke,
}: {
  projektId: string;
  anlage: DruckverlustAnlage;
  teilstreckenLaden: (a: { anlage: string; weg: string }) => Promise<LueftungWegteilstrecken>;
  waehleTeilstrecke: (t: LueftungWegteilstrecke) => void;
}) {
  const { api } = useOutletContext<Rahmen>();
  // TV7a-2: der Kasten des Nachweises dieser Anlage (LUE-DRV je Anlage, beide Wege) - wie das PDF hier rechts oben.
  const berichtsvorbehalte = useBerichtsvorbehalte(api, projektId, 'druckverlust', { anlage: anlage.anlage });
  const [strang, setStrang] = useState<DruckverlustStrang | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [befunde, setBefunde] = useState<LueftungBefundliste | null>(null);
  // `undefined` = lädt noch, `null` = keine Teilstreckenzeilen (Lauf von vor der Ablage).
  const [strecken, setStrecken] = useState<LueftungWegteilstrecken | null | undefined>(undefined);

  const laden = useCallback(() => {
    setFehler(null);
    api.druckverlustAnlage(projektId, anlage.anlage, anlage.weg).then(setStrang).catch(setFehler);
    // Befunde und Teilstrecken sind Beigaben mit eigenem catch: Ein Lauf ohne
    // sie ist kein Fehler des Nachweises, und der Strang bliebe sonst ungesehen.
    api.lueftungBefunde(projektId, { anlage: anlage.anlage, weg: anlage.weg })
      .then(setBefunde).catch(() => setBefunde(null));
    teilstreckenLaden(anlage).then(setStrecken).catch(() => setStrecken(null));
  }, [api, projektId, anlage, teilstreckenLaden]);

  useEffect(() => { laden(); }, [laden]);

  if (fehler !== null) {
    return (
      <ContentSection titel={`${anlage.anlage} · ${anlage.weg}`}>
        <ErrorState meldung="Der Strang konnte nicht abgerufen werden." technisch={fehler} beiErneutVersuchen={laden} />
      </ContentSection>
    );
  }
  if (!strang) {
    return (
      <ContentSection titel={`${anlage.anlage} · ${anlage.weg}`}>
        <LoadingState text="Strang wird abgerufen …" zeilen={6} />
      </ContentSection>
    );
  }

  const gesamt = strang.druckverlustPa ?? 0;
  const anteilReibung = gesamt > 0 ? Math.round((100 * (strang.reibungPa ?? 0)) / gesamt) : 0;

  return (
    <ContentSection
      titel={`${strang.anlage} · ${strang.weg}`}
      beschreibung={`Maßgebender Auslass ${strang.massgebenderAuslass ?? '—'}`
        + `${strang.massgebenderRaum ? ` · Raum ${strang.massgebenderRaum}` : ''}`
        + ` · Kreis ${strang.kreis ?? '—'} · ${formatiereZahl(strang.bauteile, 0)} Bauteile in der Anlage`}
      aktionen={(
        <ExportLeiste ziele={[
          { art: 'pdf', beschriftung: 'Nachweis LUE-DRV', laden: () => api.druckverlustPdf(projektId, strang.anlage) },
          {
            art: 'pdf', beschriftung: 'Anlagenschema', hinweis: 'Bauteile des Geräts in Strömungsrichtung',
            laden: () => api.anlagenschemaPdf(projektId, strang.anlage),
          },
          {
            art: 'pdf', beschriftung: 'Strangschema', hinweis: 'Abgänge und Auslässe mit Teilstrecke, Raum und Pegel',
            laden: () => api.strangschemaPdf(projektId, strang.anlage),
          },
        ]}
        />
      )}
    >
      <Berichtsvorbehaltkasten stand={berichtsvorbehalte} bericht="druckverlust" />

      <KpiReihe>
        <KpiCard titel="Druckverlust gesamt" wert={gesamt} einheit="Pa" nachkomma={0} />
        <KpiCard titel="Reibung" wert={strang.reibungPa} einheit="Pa" nachkomma={0}
                 vergleich={`${formatiereZahl(anteilReibung, 0)} %`} />
        <KpiCard titel="Einzelwiderstände" wert={strang.einzelPa} einheit="Pa" nachkomma={0}
                 vergleich={`${formatiereZahl(100 - anteilReibung, 0)} %`} />
        <KpiCard titel="Volumenstrom" wert={strang.volumenstromM3h} einheit="m³/h" nachkomma={0} />
        <KpiCard titel="Stranglänge" wert={strang.laengeM} einheit="m" nachkomma={1} />
        <KpiCard titel="Auslässe erreichbar" wert={strang.auslaesse} />
      </KpiReihe>

      {strang.hinweise.length > 0 && (
        <DataTable<{ nummer: number; text: string }>
          name="Hinweise zum Lauf"
          spalten={[
            { schluessel: 'nummer', titel: 'Nr', art: 'zahl', nachkomma: 0, wert: (h) => h.nummer },
            { schluessel: 'text', titel: 'Hinweis', wert: (h) => h.text },
          ]}
          zeilen={strang.hinweise.map((text, i) => ({ nummer: i + 1, text }))}
          schluessel={(h) => String(h.nummer)}
          dicht
        />
      )}

      <Befunde liste={befunde} projektId={projektId} />
      <Strangtabelle strang={strang} />
      {strecken && strecken.teilstrecken.length > 0 && (
        <Verbraucher strecken={strecken} waehle={waehleTeilstrecke} />
      )}
      {strecken === null && (strang.teilstrecken?.length ?? 0) > 1 && (
        <p className="lue-dv-laufwerte">
          Für diesen Weg liegen keine Teilstreckenzeilen vor ({formatiereZahl(strang.teilstrecken.length, 0)} Stränge
          stehen nur in der abgelegten Verbraucherliste des Laufs). Nach einem neuen Kanalnetzlauf steht hier die
          Aufschlüsselung je Verbraucher.
        </p>
      )}
    </ContentSection>
  );
}

/** Der maßgebende Strang Posten für Posten; der Fuß trägt die Summe des Laufs (mit fehlenden Beiwerten „≥“). */
function Strangtabelle({ strang }: { strang: DruckverlustStrang }) {
  const spalten = useMemo<Spalte<DruckverlustPosten>[]>(() => [
    { schluessel: 'nummer', titel: 'Nr', art: 'zahl', nachkomma: 0, wert: (p) => p.nummer, immerSichtbar: true },
    { schluessel: 'art', titel: 'Art', wert: (p) => p.art },
    { schluessel: 'bauform', titel: 'Bauform / Bezeichnung', wert: (p) => p.bauform ?? p.bezeichnung ?? '' },
    { schluessel: 'weg', titel: 'Weg am T-Stück', wert: (p) => (p.weg && p.weg !== 'unbekannt' ? p.weg : '') },
    { schluessel: 'element', titel: 'Element', art: 'zahl', nachkomma: 0, wert: (p) => p.elementId },
    { schluessel: 'volumenstrom', titel: 'Volumenstrom', ...spalteFuer('volumenstrom_luft'), wert: (p) => p.volumenstromM3h },
    { schluessel: 'laenge', titel: 'Länge', art: 'zahl', einheit: 'm', nachkomma: 2, wert: (p) => p.laengeM, fuss: 'summe' },
    { schluessel: 'querschnitt', titel: 'Querschnitt', art: 'zahl', einheit: 'm²', nachkomma: 4, wert: (p) => p.querschnittM2 },
    { schluessel: 'v', titel: 'Geschwindigkeit', ...spalteFuer('geschwindigkeit'), wert: (p) => p.geschwindigkeitMs },
    { schluessel: 'pd', titel: 'dyn. Druck', art: 'zahl', einheit: 'Pa', nachkomma: 1, wert: (p) => p.geschwindigkeitsdruckPa },
    { schluessel: 'lambda', titel: 'λ', art: 'zahl', nachkomma: 4, wert: (p) => p.lambda },
    { schluessel: 'zeta', titel: 'ζ', art: 'zahl', nachkomma: 2, wert: (p) => p.zeta },
    herkunftSpalte<DruckverlustPosten>('herkunft', 'Herkunft ζ', (p) => p.herkunft),
    {
      schluessel: 'dp', titel: 'Druckverlust', art: 'zahl', einheit: 'Pa', nachkomma: 2,
      wert: (p) => p.druckverlustPa,
      inhalt: (p) => (p.druckverlustPa === null && p.herkunft === 'keine'
        ? <StatusBadge zustand="fehler" text="Beiwert fehlt" klein />
        : formatiereZahl(p.druckverlustPa, 2)),
      fuss: { fest: strang.druckverlustPa, fehlend: strang.fehlendeBeiwerte, schranke: true },
    },
    { schluessel: 'summe', titel: 'laufende Summe', art: 'zahl', einheit: 'Pa', nachkomma: 1, wert: (p) => p.summePa },
    { schluessel: 'hinweis', titel: 'Hinweis', wert: (p) => p.hinweis, versteckt: true },
  ], [strang]);

  return (
    <DataTable<DruckverlustPosten>
      name="Maßgebender Strang"
      beschreibung="In Strömungsrichtung; die Summe ist der Druckverlust des Laufs. Fehlt ein Beiwert, steht sie als untere Schranke „≥“."
      spalten={spalten}
      zeilen={strang.strang}
      schluessel={(p) => `${p.nummer}|${p.elementId}`}
      ausfuhr
      ausfuhrDateiname={`druckverlust-${strang.anlage}-${strang.weg}.csv`}
      dicht
      leer={<span>Der Lauf hat für diesen Weg keinen Strang abgelegt.</span>}
    />
  );
}

/** Alle Verbraucher des Wegs - je Auslass eine Zeile (Befund L-16), ohne Kappung, sortierbar. */
function Verbraucher({ strecken, waehle }: {
  strecken: LueftungWegteilstrecken; waehle: (t: LueftungWegteilstrecke) => void;
}) {
  const s = strecken.summe;
  const spalten = useMemo<Spalte<LueftungWegteilstrecke>[]>(() => [
    { schluessel: 'nummer', titel: 'Nr', art: 'zahl', nachkomma: 0, wert: (t) => t.nummer, immerSichtbar: true },
    {
      schluessel: 'massgebend', titel: 'maßgebend', art: 'status', wert: (t) => (t.istMassgebend ? 'maßgebend' : ''),
      inhalt: (t) => (t.istMassgebend ? <StatusBadge zustand="info" text="maßgebend" klein /> : null),
    },
    { schluessel: 'raum', titel: 'Raum', wert: (t) => t.raumnummer ?? '' },
    { schluessel: 'ebene', titel: 'Ebene', wert: (t) => t.ebene ?? '' },
    {
      schluessel: 'auslass', titel: 'Auslass',
      wert: (t) => t.auslassBauform ?? (t.auslassId === null ? '' : String(t.auslassId)),
    },
    { schluessel: 'volumenstrom', titel: 'Volumenstrom', ...spalteFuer('volumenstrom_luft'), wert: (t) => t.volumenstromM3h },
    { schluessel: 'laenge', titel: 'Länge', art: 'zahl', einheit: 'm', nachkomma: 1, wert: (t) => t.laengeM },
    { schluessel: 'bauteile', titel: 'Bauteile', art: 'zahl', nachkomma: 0, wert: (t) => t.bauteile },
    { schluessel: 'dp', titel: 'Druckverlust', art: 'zahl', einheit: 'Pa', nachkomma: 1, wert: (t) => t.druckverlustPa },
    {
      schluessel: 'abgleich', titel: 'abzudrosseln', art: 'zahl', einheit: 'Pa', nachkomma: 1,
      wert: (t) => (t.abgleichPa === null || t.abgleichPa <= 0 ? null : t.abgleichPa),
      titelHinweis: 'Differenz zum maßgebenden Strang: der Druck, der an diesem Auslass beim Einregulieren wegzunehmen ist',
    },
    { schluessel: 'lwa', titel: 'L_WA Auslass', ...spalteFuer('schallpegel_dba'), wert: (t) => t.lwaAuslassDb },
    {
      schluessel: 'lpa', titel: 'L_pA direkt', ...spalteFuer('schallpegel_dba'), wert: (t) => t.lpaDirektDb,
      inhalt: (t) => (t.lpaDirektDb === null ? '—' : (
        <>
          {formatiereZahl(t.lpaDirektDb, 1)}
          {t.ueberZiel && <> <StatusBadge zustand="warnung" text="über Ziel" klein /></>}
        </>
      )),
    },
    { schluessel: 'ziel', titel: 'Zielpegel', art: 'zahl', einheit: 'dB(A)', nachkomma: 0, wert: (t) => t.zielpegelDbA },
    { schluessel: 'massgeblich', titel: 'maßgeblich', wert: (t) => t.massgeblich ?? '' },
    { schluessel: 'hinweis', titel: 'Hinweis', wert: (t) => t.hinweis ?? '', versteckt: true },
  ], []);

  return (
    <DataTable<LueftungWegteilstrecke>
      name="Verbraucher des Wegs"
      beschreibung={`Je Auslass ein Strang (${formatiereZahl(s.strecken, 0)}); eine Zeile wählen zeigt seinen Strang. „abzudrosseln“ ist die Differenz zum maßgebenden Strang.`
        + (s.ueberZiel > 0
          ? ` ${formatiereZahl(s.ueberZiel, 0)} Auslass/Auslässe über ihrem Zielpegel; der Raumpegel kann höher sein, weil sich die Auslässe eines Raums addieren.`
          : '')
        + (strecken.schallQuelle === 'keine' ? ' Für diesen Lauf liegen keine Schallwerte vor.' : '')}
      spalten={spalten}
      zeilen={strecken.teilstrecken}
      schluessel={(t) => t.id}
      sortierung={{ spalte: 'dp', richtung: 'ab' }}
      beiZeileKlick={waehle}
      zeilenKlasse={(t) => (t.istMassgebend ? 'lue-dv-zeile-massgebend' : undefined)}
      ausfuhr
      ausfuhrDateiname={`verbraucher-${strecken.anlage}-${strecken.weg}.csv`}
      suchePlatzhalter="Raum, Auslass …"
      leer={<span>Keine Teilstrecken.</span>}
    />
  );
}

/** Die Befunde der Grenzwertprüfung zu diesem Weg; alle Befunde des Laufs stehen auf der eigenen Seite. */
function Befunde({ liste, projektId }: { liste: LueftungBefundliste | null; projektId: string }) {
  const spalten = useMemo<Spalte<LueftungBefund>[]>(() => [
    {
      schluessel: 'schwere', titel: 'Schwere', art: 'status', wert: (b) => schwereStatus(b.schwere).text,
      inhalt: (b) => <StatusBadge {...schwereStatus(b.schwere)} klein />, immerSichtbar: true,
    },
    { schluessel: 'regel', titel: 'Regel', wert: (b) => b.regel },
    { schluessel: 'bauteil', titel: 'Bauteil', wert: (b) => b.bauform ?? b.art ?? '' },
    { schluessel: 'element', titel: 'Element', art: 'zahl', nachkomma: 0, wert: (b) => b.elementId },
    { schluessel: 'raum', titel: 'Raum', wert: (b) => b.raumnummer ?? '' },
    { schluessel: 'ist', titel: 'Ist', art: 'zahl', nachkomma: 2, wert: (b) => b.istwert },
    { schluessel: 'grenze', titel: 'Grenze', art: 'zahl', nachkomma: 1, wert: (b) => b.grenzwert },
    { schluessel: 'einheit', titel: 'Einheit', wert: (b) => b.einheit },
    { schluessel: 'text', titel: 'Meldung', wert: (b) => b.text },
  ], []);

  // Ohne Lauf keine Liste: Ein leerer Kasten mit „0 Befunde“ läse sich wie „geprüft und in Ordnung“.
  if (!liste || liste.summe.gesamt === 0) return null;

  return (
    <DataTable<LueftungBefund>
      name="Befunde der Grenzwertprüfung"
      beschreibung="Geschwindigkeit, Kennfeld, Raumpegel und Hygiene zu diesem Weg. Die Grenzen sind unbestätigte Annahmen - eine Überschreitung ist eine Anzeige, kein Mangel."
      trefferText={liste.gekuerzt
        ? `${formatiereZahl(liste.befunde.length, 0)} von ${formatiereZahl(liste.summe.gesamt, 0)} gezeigt - alle auf der Seite der Befunde`
        : undefined}
      spalten={spalten}
      zeilen={liste.befunde}
      schluessel={(b) => `${b.elementId}|${b.regel}|${b.weg ?? ''}`}
      ausfuhr
      werkzeuge={<Link to={`/projekte/${projektId}/lueftung/pruefbefunde`}>alle Befunde des Laufs</Link>}
    />
  );
}

// ═══ Rechts: eine Teilstrecke ════════════════════════════════════════════════

function Teilstreckendetail({ projektId, teilstrecke, zurueck }: {
  projektId: string; teilstrecke: LueftungWegteilstrecke; zurueck: () => void;
}) {
  const { api } = useOutletContext<Rahmen>();
  const [daten, setDaten] = useState<LueftungTeilstreckenposten | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);

  const laden = useCallback(() => {
    setFehler(null);
    api.lueftungTeilstreckenposten(projektId, teilstrecke.id).then(setDaten).catch(setFehler);
  }, [api, projektId, teilstrecke.id]);

  useEffect(() => { laden(); }, [laden]);

  const spalten = useMemo<Spalte<LueftungPosten>[]>(() => [
    { schluessel: 'nummer', titel: 'Nr', art: 'zahl', nachkomma: 0, wert: (p) => p.nummer, immerSichtbar: true },
    { schluessel: 'art', titel: 'Art', wert: (p) => p.art },
    { schluessel: 'bauform', titel: 'Bauform / Typ', wert: (p) => p.bauform ?? p.typ ?? p.familie ?? '' },
    { schluessel: 'element', titel: 'Element', art: 'zahl', nachkomma: 0, wert: (p) => p.elementId },
    { schluessel: 'volumenstrom', titel: 'Volumenstrom', ...spalteFuer('volumenstrom_luft'), wert: (p) => p.volumenstromM3h },
    herkunftSpalte<LueftungPosten>('vHerkunft', 'Herkunft V', (p) => p.volumenstromHerkunft),
    { schluessel: 'querschnitt', titel: 'Querschnitt', art: 'zahl', einheit: 'm²', nachkomma: 4, wert: (p) => p.querschnittM2 },
    herkunftSpalte<LueftungPosten>('aHerkunft', 'Herkunft A', (p) => p.querschnittHerkunft),
    { schluessel: 'laenge', titel: 'Länge', art: 'zahl', einheit: 'm', nachkomma: 2, wert: (p) => p.laengeM, fuss: 'summe' },
    { schluessel: 'v', titel: 'Geschwindigkeit', ...spalteFuer('geschwindigkeit'), wert: (p) => p.geschwindigkeitMs },
    { schluessel: 're', titel: 'Re', art: 'zahl', nachkomma: 0, wert: (p) => p.reynolds },
    { schluessel: 'lambda', titel: 'λ', art: 'zahl', nachkomma: 4, wert: (p) => p.lambda },
    { schluessel: 'zeta', titel: 'ζ', art: 'zahl', nachkomma: 2, wert: (p) => p.zeta },
    herkunftSpalte<LueftungPosten>('zHerkunft', 'Herkunft ζ', (p) => p.beiwertHerkunft),
    { schluessel: 'weg', titel: 'Weg am T-Stück', wert: (p) => (p.weg && p.weg !== 'unbekannt' ? p.weg : '') },
    {
      schluessel: 'dp', titel: 'Druckverlust', art: 'zahl', einheit: 'Pa', nachkomma: 2,
      wert: (p) => p.druckverlustPa,
      inhalt: (p) => (p.druckverlustPa === null && p.beiwertHerkunft === 'keine'
        ? <StatusBadge zustand="fehler" text="Beiwert fehlt" klein />
        : formatiereZahl(p.druckverlustPa, 2)),
      fuss: daten ? { fest: daten.summe.summePa, fehlend: daten.summe.ohneBeiwert, schranke: true } : undefined,
    },
    {
      schluessel: 'einzel', titel: 'Einzelbewertung ohne Weg', art: 'zahl', einheit: 'Pa', nachkomma: 2,
      wert: (p) => p.druckverlustEinzelbewertungPa,
      titelHinweis: 'Der Wert am Bauteil: Er kennt den Weg durch das Bauteil nicht und rechnet am T-Stück mit dem ungünstigeren Beiwert',
    },
    { schluessel: 'summe', titel: 'laufende Summe', art: 'zahl', einheit: 'Pa', nachkomma: 1, wert: (p) => p.summePa },
    { schluessel: 'hinweis', titel: 'Hinweis', wert: (p) => p.hinweis ?? '', versteckt: true },
  ], [daten]);

  const t = daten?.teilstrecke;

  return (
    <ContentSection
      titel={`Teilstrecke ${t?.kennzeichen ?? teilstrecke.kennzeichen}`}
      beschreibung={t?.bezeichnung ?? undefined}
      aktionen={<button type="button" className="m-knopf" onClick={zurueck}>zurück zum Weg</button>}
    >
      {fehler !== null && (
        <ErrorState meldung="Die Teilstrecke konnte nicht abgerufen werden." technisch={fehler} beiErneutVersuchen={laden} />
      )}
      {!daten && fehler === null && <LoadingState text="Teilstrecke wird abgerufen …" zeilen={6} />}
      {daten && t && (
        <>
          <KpiReihe>
            <KpiCard titel="Druckverlust" wert={t.druckverlustPa} einheit="Pa" nachkomma={1}
                     vergleich={t.istMassgebend ? 'maßgebender Strang' : undefined} />
            <KpiCard titel="Volumenstrom" wert={t.volumenstromM3h} einheit="m³/h" nachkomma={0} />
            <KpiCard titel="Länge" wert={t.laengeM} einheit="m" nachkomma={1} />
            <KpiCard titel="Bauteile" wert={t.bauteile} />
            <KpiCard titel="Σ ζ" wert={t.summeZeta} nachkomma={2} />
            <KpiCard
              titel="Posten ohne Beiwert" wert={daten.summe.ohneBeiwert}
              vergleich={`${formatiereZahl(daten.summe.abweichendeEinzelwerte, 0)} mit abweichender Einzelbewertung`}
            />
          </KpiReihe>

          {t.hinweis && <p className="lue-dv-laufwerte">{t.hinweis}</p>}

          {daten.posten.length === 0 ? (
            <EmptyState klein titel="Kein Strang zu dieser Teilstrecke." erklaerung={daten.grund ?? undefined} />
          ) : (
            <DataTable<LueftungPosten>
              name="Strang der Teilstrecke"
              beschreibung="In Strömungsrichtung. Die Herkunft je Eingangswert sagt, ob er aus dem Modell, einem Datenblatt, einer Bauformregel oder dem Nachbarbauteil stammt oder gerechnet ist. Druckverlust ist der Beitrag auf DIESEM Weg."
              spalten={spalten}
              zeilen={daten.posten}
              schluessel={(p) => `${p.elementId}|${p.nummer ?? ''}`}
              ausfuhr
              dicht
            />
          )}
        </>
      )}
    </ContentSection>
  );
}

function herkunftSpalte<Z>(schluessel: string, titel: string, wert: (z: Z) => string | null): Spalte<Z> {
  return {
    schluessel, titel, art: 'status', wert: (z) => postenherkunft(wert(z)).text,
    inhalt: (z) => <StatusBadge {...postenherkunft(wert(z))} klein />,
  };
}

// ═══ Annahmen und Vorgaben: die Laufparameter (S1.6), weiter änderbar ═══════════
//
// Die Temperatur steht als LAUFPARAMETER (Festlegung des Fachplaners vom
// 16.09.2026 zu Frage 6: „Ja; änderbar“), und darunter steht, was die Rechnung
// daraus bildet. Geändert wird im Schubfach über `Feld` (S1.10 Nr. 4: Feld mit
// Beschriftung, Fokus zurück); der Wert geht als Text an den Server wie vorher.
// Das Speichern schreibt eine Zeile, es rechnet nichts: Die abgelegten
// Druckverluste bleiben bis zum nächsten Lauf die des alten Werts.

function Laufparameter({ daten, setzen }: {
  daten: LueftungGrundlagen | null;
  setzen: (schluessel: string, wert: string) => Promise<void>;
}) {
  const [offen, setOffen] = useState<LueftungLaufparameter | null>(null);
  const [eingabe, setEingabe] = useState('');
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const [gespeichert, setGespeichert] = useState<string | null>(null);

  const oeffnen = useCallback((p: LueftungLaufparameter) => {
    setOffen(p);
    setEingabe(p.wert);
    setFehler(null);
  }, []);

  const speichern = async () => {
    if (!offen) return;
    const wert = eingabe.trim();
    if (wert.length === 0 || wert === offen.wert) return;
    setSpeichert(true);
    setFehler(null);
    try {
      await setzen(offen.schluessel, wert);
      setGespeichert(`${offen.bezeichnung ?? offen.schluessel}: ${wert}${offen.einheit ? ` ${offen.einheit}` : ''} gespeichert`
        + ' - gilt ab dem nächsten Rechenlauf.');
      setOffen(null);
    } catch (e) {
      setFehler(e);
    } finally {
      setSpeichert(false);
    }
  };

  const spalten = useMemo<Spalte<LueftungLaufparameter>[]>(() => [
    { schluessel: 'parameter', titel: 'Parameter', wert: (p) => p.bezeichnung ?? p.schluessel, immerSichtbar: true },
    { schluessel: 'schluessel', titel: 'Schlüssel', wert: (p) => p.schluessel },
    { schluessel: 'wert', titel: 'Wert', wert: (p) => p.wert },
    { schluessel: 'einheit', titel: 'Einheit', wert: (p) => p.einheit ?? '' },
    { schluessel: 'herkunft', titel: 'Herkunft', wert: (p) => (p.istProjektwert ? 'Projektwert' : 'hausweit') },
    { schluessel: 'quelle', titel: 'Quelle / Norm', wert: (p) => p.quelle ?? p.begruendung ?? '' },
    {
      schluessel: 'bestaetigt', titel: 'Bestätigung', art: 'status', wert: (p) => bestaetigung(p.istBestaetigt).text,
      inhalt: (p) => <StatusBadge {...bestaetigung(p.istBestaetigt)} klein />,
    },
    {
      schluessel: 'aenderung', titel: 'Änderung', sortierbar: false, wert: () => '',
      inhalt: (p) => (
        <button type="button" className="m-knopf" onClick={() => oeffnen(p)}
                aria-label={`${p.bezeichnung ?? p.schluessel} ändern`}>
          ändern
        </button>
      ),
    },
  ], [oeffnen]);

  if (!daten || daten.laufparameter.length === 0) return null;

  const zustand = (z: { temperaturC: number; dichteKgM3: number } | null) => (z === null ? '—'
    : `${formatiereZahl(z.temperaturC, 1)} °C → ρ = ${formatiereZahl(z.dichteKgM3, 3)} kg/m³`);

  return (
    <ContentSection
      id={ANNAHMEN_ANKER}
      titel={ANNAHMEN_TITEL}
      beschreibung="Die Laufparameter, aus denen die Rechnung ihre Zahlen bildet. Ein geänderter Wert wird als Projektwert abgelegt (der hausweite Satz bleibt stehen) und gilt ab dem nächsten Rechenlauf."
    >
      <DataTable<LueftungLaufparameter>
        name="Laufparameter"
        spalten={spalten}
        zeilen={daten.laufparameter}
        schluessel={(p) => p.schluessel}
        leer={<span>Keine Laufparameter.</span>}
      />
      <p className="lue-dv-laufwerte" role="status" data-meldung="laufparameter">{gespeichert ?? ''}</p>
      <p className="lue-dv-laufwerte">
        Daraus rechnet der nächste Lauf: Außenluft {zustand(daten.gerechnet.aussenluft)}, Fortluft
        {' '}{zustand(daten.gerechnet.fortluft)} (ideales Gasgesetz bei 101.325 Pa, Zähigkeit nach Sutherland).
        {' '}Ein gespeicherter Wert gilt ab dem nächsten Rechenlauf; die abgelegten Druckverluste bleiben bis dahin
        {' '}die des alten Werts. Dazu {formatiereZahl(daten.medienwerte.length, 0)} Medienwerte und
        {' '}{formatiereZahl(daten.hygieneregeln.length, 0)} Hygieneregeln des Bestands.
      </p>

      <Drawer
        offen={offen !== null}
        titel="Laufparameter ändern"
        beschreibung={offen ? `${offen.bezeichnung ?? offen.schluessel} (${offen.schluessel})` : undefined}
        beiSchliessen={() => setOffen(null)}
        fuss={(
          <>
            <button type="button" className="m-knopf" onClick={() => setOffen(null)} disabled={speichert}>
              Abbrechen
            </button>
            <button
              type="button" className="m-knopf m-knopf-haupt" onClick={() => void speichern()}
              disabled={speichert || !offen || eingabe.trim().length === 0 || eingabe.trim() === offen.wert}
            >
              {speichert ? 'speichert …' : 'speichern'}
            </button>
          </>
        )}
      >
        {offen && (
          <>
            <Feld
              beschriftung={offen.einheit ? `Wert (${offen.einheit})` : 'Wert'}
              wert={eingabe}
              beiAenderung={(w) => setEingabe(typeof w === 'string' ? w : String(w ?? ''))}
              hinweis={`Bisher ${offen.wert}${offen.einheit ? ` ${offen.einheit}` : ''}, `
                + `${offen.istProjektwert ? 'Projektwert' : 'hausweiter Wert'}. Der neue Wert wird als Projektwert `
                + 'abgelegt und gilt ab dem nächsten Rechenlauf.'}
              pflicht
              breite="voll"
            />
            {fehler !== null && (
              <ErrorState klein meldung="Der Wert konnte nicht gespeichert werden." technisch={fehler} />
            )}
          </>
        )}
      </Drawer>
    </ContentSection>
  );
}
